import _ from "lodash";
import React, { useEffect, useState } from "react";
import { Link, Redirect, withRouter } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import firstBy from "thenby";
import moment from "moment";
import classNames from "classnames";
import queryString from "query-string";

import {
  CustomerSucessController,
  ModalConfirm,
  Button as BtnPrimary,
  EmptyStateView,
  PosthogStore
} from "@talentbait/shared-components";

import { withLDConsumer } from "launchdarkly-react-client-sdk";

import logger from "../../../utils/LoggerFrontend";
import StatusStore from "../../../Stores/StatusStore";
import {
  renderStatus as renderStatusUtils,
  renderStatusSort,
  sumValueFromDictKey,
  getStatusInfos,
  formatIntlNumber
} from "../../../utils/Helpers";
import DashboardStore, {
  ALL_STATUSES,
  STATUSES_TO_LOAD_AT_BEGINNING
} from "../../Dashboard/DashboardStore";
import DashboardActions from "../../Dashboard/DashboardActions";
import Actions from "../../../Actions";

import {
  getCostBreakdown,
  getLastMonthCampaigns,
  getLastSixMonthCampaigns,
  getYearCampaigns
} from "../../Dashboard/utils";

// Views
import LoadingView from "../../../Components/LoadingView";
import DashboardFilters from "../../Dashboard/DashboardFilters.jsx";

import { ACTION_MESSAGES, DEFAULT_USER_PERMISSIONS } from "../../../Permissions/constants.js";
import { saveUserFlatLogService } from "../../../services/UserLogFlatService.js";
import { getPermissions } from "../../../Permissions/index.js";
import { useTranslation } from "react-i18next";
import { TrashIcon } from "../../../Components/Icons";
import { Tooltip } from "../../../Components/Overlays";
import { badgeStatus } from "../../Dashboard/DashboardStatuses.js";
import {
  BrandKitMessage,
  BrandKitMessageWrapper,
  Emoji,
  LikesBadge
} from "../../Dashboard/Dashboard.styled.js";
import Snowfall from "react-snowfall";
import { StatusBadge } from "./AdAssistantDashboard.styled.js";
import { withRefluxStore } from "../../../Stores";
import AdAssistantStore from "../AdAssistantStore.js";
import { useAdAssistantDashboard } from "../hooks/useAdAssistantDashboard.jsx";
import { handleNewAssistantFlowFlag } from "../../../utils/handleNewAssistantFlowFlag.js";

const filterCampaigns = ({ campaigns, statuses, companies, owners, dates }) => {
  let filteredCampaigns = [...campaigns];
  companies = companies.filter((company) => company !== "");
  owners = owners.filter((owner) => owner !== "");
  dates = dates.filter(Boolean);

  if (statuses?.length) {
    filteredCampaigns = filteredCampaigns.filter((campaign) => statuses.includes(campaign.status));
  }

  if (companies?.length) {
    filteredCampaigns = filteredCampaigns.filter((campaign) =>
      companies.includes(campaign.companyName)
    );
  }

  if (owners?.length) {
    filteredCampaigns = filteredCampaigns.filter((campaign) => owners.includes(campaign.userName));
  }

  if (dates.length) {
    let yearsFilter = [];

    dates.forEach((date) => {
      if (date === "lastMonth") {
        filteredCampaigns = getLastMonthCampaigns(filteredCampaigns);
      } else if (date === "lastSixMonths") {
        filteredCampaigns = getLastSixMonthCampaigns(filteredCampaigns);
      } else {
        const year = parseInt(date, 10);
        yearsFilter.push(year);
      }
    });

    filteredCampaigns = getYearCampaigns(filteredCampaigns, yearsFilter);
  }

  return filteredCampaigns;
};

const AllCampaignsDashboard = ({
  user,
  userProfile,
  organization,
  posthogInstance,
  filters,
  sortBy,
  sortOrder,
  campaignList,
  errorLoadingCampaigns,
  isAdminView,
  orgaId,
  flags,
  history
}) => {
  const { t } = useTranslation();

  const { campaigns: assistantCampaigns, removeCampaignById } = useAdAssistantDashboard(
    organization,
    userProfile
  );
  const oldCampaignList = campaignList || [];
  const allCampaignList = [...oldCampaignList, ...assistantCampaigns];

  // States from original component
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [timeWhenLoadingCampaigns, setTimeWhenLoadingCampaigns] = useState(null);

  // Handle loading campaigns
  useEffect(() => {
    const loadHandlerStart = Actions.loadFilteredCampaigns.listen(() => {
      setIsLoadingCampaigns(true);
      setTimeWhenLoadingCampaigns(Date.now());
    });

    const loadHandlerComplete = Actions.loadFilteredCampaigns.completed.listen(() => {
      const currentTime = Date.now();
      const timeInMilliseconds = currentTime - timeWhenLoadingCampaigns;
      const statuses = filters;

      logger.log("Dashboard", "Elapsed milliseconds to complete fetching campaigns", {
        timeInMilliseconds,
        statuses
      });

      setIsLoadingCampaigns(false);
      setTimeWhenLoadingCampaigns(null);
    });

    const failedHandler = Actions.loadFilteredCampaigns.failed.listen(() => {
      setIsLoadingCampaigns(false);
    });

    const changeOrgHandler = Actions.changeDefaultOrganization.listen(onChangeOrganization);

    // Clean up listeners
    return () => {
      logger.debug("Dashboard:componentWillUnmount", "called...");
      loadHandlerStart();
      loadHandlerComplete();
      failedHandler();
      changeOrgHandler();
    };
  }, [timeWhenLoadingCampaigns, filters]);

  // Initial load and URL params handling
  useEffect(() => {
    const showPrepaidModal = new URLSearchParams(location.search).get("prepaid");
    const membersModal = new URLSearchParams(location.search).get("members");

    const queryFilters = queryString.parse(location.search) || {};

    const queryStatuses = queryFilters?.statuses?.split(",").filter((status) => status !== "");
    const queryCompanies = queryFilters?.companies?.split(",").filter((company) => company !== "");
    const queryOwners = queryFilters?.owners?.split(",").filter((owner) => owner !== "");

    let newCompanies = new Set(
      queryCompanies && queryCompanies !== filters.companies ? queryCompanies : filters.companies
    );

    if (organization?.clientType === "ats-client" && organization.name) {
      newCompanies.add(organization.name);
    }

    const newFilters = {
      statuses: isAdminView
        ? []
        : queryStatuses && queryStatuses !== filters.statuses
        ? queryStatuses
        : filters.statuses,
      companies: Array.from(newCompanies),
      owners: queryOwners && queryOwners !== filters.owners ? queryOwners : filters.owners
    };

    DashboardActions.handleFilters(newFilters);

    logger.debug("Dashboard:componentDidMount", "called...", {
      userProfile
    });

    if (!campaignList && userProfile && isLoadingCampaigns === false && organization) {
      logger.debug("Dashboard:componentDidMount", "Loads campaigns for orga: ", {
        userProfile,
        organization
      });

      setIsLoadingCampaigns(true);

      Actions.loadFilteredCampaigns({
        organizationId: orgaId || userProfile.organizationId,
        filters: {
          ...newFilters,
          statuses: newFilters.statuses.length === 0 ? ALL_STATUSES : STATUSES_TO_LOAD_AT_BEGINNING
        }
      });

      if (newFilters.statuses.length === 0) {
        DashboardActions.setFinishedCampaignsHaveBeenLoaded(true);
      }

      CustomerSucessController.send("Dashboard:load", {});
    } else {
      logger.log("warn:Dashboard:componentDidUpdate", "Didnt load campaign for orga ", {
        organizationId: orgaId || (userProfile && userProfile.organizationId),
        isLoadingCampaigns
      });
    }

    if (campaignList && showPrepaidModal) {
      Actions.showCreditModal();
    }

    if (campaignList && membersModal) {
      membersModal === "popup" ? Actions.showInviteModal() : Actions.showInviteModalHelp();
    }
  }, [campaignList, userProfile, organization, isLoadingCampaigns]);

  // Handle errorLoadingCampaigns and loading state
  useEffect(() => {
    if (errorLoadingCampaigns) {
      return;
    }

    if (!campaignList && userProfile && !isLoadingCampaigns && organization) {
      setIsLoadingCampaigns(true);

      if (organization.clientType === "ats-client") {
        Actions.loadFilteredCampaigns({
          organizationId: orgaId || userProfile.organizationId,
          filters: {
            ...filters,
            companies: [organization.name],
            statuses: filters.statuses.length === 0 ? ALL_STATUSES : STATUSES_TO_LOAD_AT_BEGINNING
          }
        });
      } else {
        Actions.loadFilteredCampaigns({
          organizationId: orgaId || userProfile.organizationId,
          filters: {
            ...filters,
            statuses: filters.statuses.length === 0 ? ALL_STATUSES : STATUSES_TO_LOAD_AT_BEGINNING
          }
        });
      }

      if (filters.statuses.length === 0) {
        DashboardActions.setFinishedCampaignsHaveBeenLoaded(true);
      }

      logger.debug("Dashboard:componentDidUpdate", "CustomerSucessController...");

      CustomerSucessController.send("Dashboard:load", {});

      saveUserFlatLogService({
        userProfile,
        orgaId: organization.id,
        orgaName: organization.name,
        actionType: ACTION_MESSAGES.VIEW.DASHBOARD.TYPE,
        message: ACTION_MESSAGES.VIEW.DASHBOARD.SUCCESS_ACTION
      });
    }
  }, [errorLoadingCampaigns, campaignList, userProfile, isLoadingCampaigns, organization]);

  const onChangeOrganization = (organizationId) => {
    DashboardActions.resetFilters();

    Actions.loadFilteredCampaigns({
      organizationId: organizationId,
      filters: { statuses: STATUSES_TO_LOAD_AT_BEGINNING }
    });
  };

  const renderStatus = (status) => {
    if (status == "ACTION_REQUIRED") {
      return t("In progress");
    }

    return renderStatusUtils(status, t);
  };

  const renderPath = ({ orgaId, campaignId, status, type, step, isOnboardingCampaign }) => {
    const { isAgency } = organization;

    if (type === "assistant" && !isAgency) {
      let route = `/assistant/${step || "preview"}/${campaignId}`;

      // This redirect autofills the form with the data from the draft campaign
      if (isOnboardingCampaign && step === "form") {
        route = `/assistant/${campaignId}`;
      }

      if (status !== "DRAFT") {
        route = `/campaign/${orgaId}/${campaignId}`;
      }

      return step === "budget-check" ? `${route}?budgetCheck=true` : route;
    }

    if (status == "DRAFT") {
      return `/new-campaign/${campaignId}`;
    }

    if (isAdminView) {
      return `/admin-campaign/${orgaId}/${campaignId}`;
    }

    return `/campaign/${orgaId}/${campaignId}`;
  };

  const getClicks = ({ metrics }) => {
    return sumValueFromDictKey(metrics || {}, "totalClicks");
  };

  const getImpressions = ({ metrics }) => {
    return sumValueFromDictKey(metrics || {}, "totalImpressions");
  };

  const getCTR = ({ metrics }) => {
    const totalImpressions = sumValueFromDictKey(metrics || {}, "totalImpressions");
    const totalClicks = sumValueFromDictKey(metrics || {}, "totalClicks");
    let ctr = 0;
    try {
      ctr = ((totalClicks / totalImpressions || 0) * 100).toFixed(2);
    } catch (error) {
      ctr = 0;
    }
    return ctr;
  };

  const getLikes = ({ metrics }) => {
    return sumValueFromDictKey(metrics || {}, "totalLikes");
  };

  const renderDraftCell = (orgaId, campaignId, campaignData) => {
    const { status = "", step = "", type = "", isOnboardingCampaign = false } = campaignData;
    const draftStatus = status === "DRAFT";
    const { hasPermissionsToRenderDraft } = getPermissions(userProfile);

    const link = (
      <Link
        to={renderPath({ orgaId, campaignId, status, type, step, isOnboardingCampaign })}
        className="dashboard__btn-small"
      >
        {draftStatus ? t("Edit") : "Details"}
      </Link>
    );

    const deleteDraft = draftStatus && renderDeleteDraft(campaignId, orgaId, campaignData);

    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        {link}
        {hasPermissionsToRenderDraft && deleteDraft}
      </div>
    );
  };

  const handleDeleteDraft = (campaignId, orgaId, campaignData) => {
    const { jobTitle, companyName } = campaignData;
    const { userName = "" } = userProfile;
    const { name: orgaName = "" } = organization;

    Actions.deleteDraftCampaign(campaignId, orgaId, removeCampaignById);
    Actions.toggleModal("showGenericModal", false);
    Actions.notifiyEvent({
      eventName: "entwurf",
      text: `📋🗑 Draft campaign deleted for *${jobTitle}*/${companyName} by *${userName}* (${orgaName}) `
    });
  };

  const handleClickOnAdminDraftCampaign = () => {
    Actions.addNotification(t("You cannot open a DRAFT in the admin view."), "error");
  };

  const nameToSlug = (name) => {
    return (name || "").toLowerCase().replace(/\s/g, "-");
  };

  const renderDeleteDraft = (campaignId, orgaId, campaignData) => {
    return (
      <TrashIcon
        id={nameToSlug(campaignData.campaignName)}
        styling={{ marginLeft: "5px" }}
        onClick={() => {
          Actions.toggleModal("showGenericModal", true, {
            headline: t("Delete draft"),
            widthVersion: "small",
            view: (
              <ModalConfirm
                showDescription={true}
                subtitle={campaignData.campaignName}
                labelConfirm={t("Yes, delete")}
                description={t("Are you sure you want to delete this draft campaign?")}
                cancelledAction={() => {
                  Actions.toggleModal("showGenericModal", false);
                }}
                confirmedAction={() => handleDeleteDraft(campaignId, orgaId, campaignData)}
              />
            )
          });
        }}
      />
    );
  };

  const renderRows = (campaigns) => {
    return campaigns?.map((campaignData) => {
      const {
        id,
        status,
        lastEditStage,
        createdDate,
        organizationId,
        campaignName,
        rechnungsNummer,
        poNumber,
        companyName,
        jobPostingUrl,
        lifeTimeBudget,
        combinedBudget,
        paymentMethod,
        applicantsCount,
        userName,
        locations,
        feedbackAnalysis,
        optimizeAnalysis,
        spotifyHumanVoice,
        channels,
        type,
        step,
        isOnboardingCampaign
      } = campaignData;

      const key = id;

      logger.debug("Dashboard", "get campaign data", { campaignData, campaignId: key });

      const nrLikes = formatIntlNumber(getLikes(campaignData), {
        language: userProfile?.language
      });

      const campaignBudget = getCostBreakdown({
        combinedBudget,
        locations,
        lifeTimeBudget,
        feedbackAnalysis,
        optimizeAnalysis,
        spotifyHumanVoice: channels?.includes("spotify") ? spotifyHumanVoice : 0,
        lastEditStage
      });

      const draftStatus = status === "DRAFT";

      return (
        <tr key={key} className="table__tr-body" id={"row-" + nameToSlug(campaignName)}>
          <td className="relative">
            {isAdminView && draftStatus ? (
              <span onClick={handleClickOnAdminDraftCampaign} className="dashboard-link">
                {campaignName} <br />
              </span>
            ) : (
              <Link
                to={renderPath({
                  orgaId: organizationId,
                  campaignId: key,
                  status,
                  type,
                  step,
                  isOnboardingCampaign
                })}
              >
                {campaignName} <br />
              </Link>
            )}

            <span className="thin">{companyName}</span>

            {nrLikes >= 20 && nrLikes < 30 && (
              <LikesBadge background="#ffb40b">
                {t("Several Likes")}
                <Emoji role="img" aria-label="success">
                  🎉
                </Emoji>
              </LikesBadge>
            )}

            {nrLikes > 30 && (
              <LikesBadge background="#8bc34a">
                {t("Many likes")}
                <Emoji role="img" aria-label="success">
                  🎉
                </Emoji>
              </LikesBadge>
            )}

            {applicantsCount > 30 && (
              <LikesBadge background="#8bc34a">
                {t("Many applicants")}
                <Emoji role="img" aria-label="success">
                  🎉
                </Emoji>
              </LikesBadge>
            )}
          </td>
          <td className="table__td-tiny">
            <a
              href={jobPostingUrl}
              target="_blank"
              alt="Stellenanzeige öffnen"
              className="table__link"
              rel="noreferrer"
            >
              <i className="fa fa-external-link" />
            </a>
          </td>

          <td className="right table__td-small thin">
            €
            {formatIntlNumber(campaignBudget, {
              language: userProfile?.language
            })}
            <br />
            <span className="dashboard__text-light">{t("Package")}</span>
          </td>

          {organization && organization.atsAccess && (
            <td className="right table__td-small relative">
              {formatIntlNumber(applicantsCount, {
                language: userProfile?.language
              })}
              <br />
              <span className="dashboard__text-light">{t("Applicants")}</span>
            </td>
          )}
          <td className="right table__td-small">
            {status !== "IN_PROGRESS"
              ? formatIntlNumber(getClicks(campaignData), {
                  language: userProfile?.language
                })
              : "-"}
            <br />

            <span className="dashboard__text-light">{t("Clicks")}</span>
          </td>
          <td className="right table__td-small">
            {status !== "IN_PROGRESS"
              ? formatIntlNumber(getImpressions(campaignData), {
                  language: userProfile?.language
                })
              : "-"}
            <br />

            <span className="dashboard__text-light">{t("Impressions")}</span>
          </td>

          <td className="right table__td-small">
            {status !== "IN_PROGRESS" ? nrLikes || 0 : "-"}
            <br />

            <span className="dashboard__text-light">{t("Likes")}</span>
          </td>

          <td className="right table__td-small">
            {status !== "IN_PROGRESS"
              ? formatIntlNumber(getCTR(campaignData), {
                  language: userProfile?.language,
                  points: 2
                })
              : "-"}
            %
            <br />
            <span className="dashboard__text-light">CTR</span>
          </td>

          <td className="center table__td-small">
            <Tooltip
              label={getStatusInfos(status, t)}
              color={badgeStatus[status].color}
              bgColor={badgeStatus[status].background}
              enabled={getStatusInfos(status, t) !== ""}
            >
              <StatusBadge status={status}>{renderStatus(status)}</StatusBadge>
            </Tooltip>
          </td>
          <td className="dashboard__date table__td-small">{renderDateCell(createdDate)}</td>
          <td className="center">{userName}</td>
          <td className="center">
            {rechnungsNummer}
            <br />
            {poNumber && <span className="dashboard__text-light">PO: {poNumber}</span>}
            <br />
            {paymentMethod && (
              <span className="dashboard__text-light">{`${renderPaymentTypeLabel(
                paymentMethod
              )}`}</span>
            )}
          </td>
          <td className="center">{renderDraftCell(organizationId, key, campaignData)}</td>
        </tr>
      );
    });
  };

  const renderPaymentTypeLabel = (paymentType) => {
    if (paymentType === "invoice") {
      return t("Invoice");
    }
    if (paymentType === "credit") {
      return t("Credit");
    }
    if (paymentType === "partial_credit") {
      return t("Partial Credit");
    }
  };

  const renderDateCell = (createdDate) => {
    return (
      <span>
        <span>
          {moment(createdDate, "YYYY-MM-DD").format("DD.MM.YYYY")} <br />
        </span>
      </span>
    );
  };

  const renderPlaceholder = () => {
    return (
      <tr>
        <td colSpan={13}>
          <div className="center campaign-read__empty-search">
            {`${t("No campaigns with the search term")} "`}
            <strong>{searchTerm}</strong>
            {`" ${t("found")}`}
          </div>
        </td>
      </tr>
    );
  };

  const handleSortBy = (newSortField, newSortOrder) => {
    // If the old sortBy field is equal to the new search field, we just
    // toggle the sort order.
    if (sortBy === newSortField) {
      newSortOrder = sortOrder === "ASC" ? "DESC" : "ASC";
    }

    DashboardActions.updateSortBy(newSortField);
    DashboardActions.updateSortOrder(newSortOrder);
  };

  const renderCampaignTable = () => {
    logger.debug("Dashboard:renderCampaignTable", "called: ", allCampaignList);
    //
    // If the campaign list is empty we show the fallback view
    //

    let result = [];

    // Turn the list of campaigns into a proper format to work with.
    for (let campaignId in allCampaignList) {
      if (allCampaignList[campaignId].id === "jGFwhQs9tYsDxBLyW8gd") continue; //TODO: remove this once GoodMills assistant campaign is live
      result.push(_.extend({}, { id: campaignId }, allCampaignList[campaignId]));
    }

    let sortedArray = _.map(result, (el) => {
      el.dateSorted = new Date(el.campaignLiveDate);
      el.statusSort = renderStatusSort(el.status);
      el.totalClicks = getClicks(el);
      return el;
    });

    if (sortBy === "createdDate") {
      // Sort by created date
      sortedArray = sortedArray.sort(firstBy("createdDate"));
      if (sortOrder === "DESC") {
        sortedArray = sortedArray.reverse();
      }
    } else if (sortBy === "name") {
      // Sort by jobTitle
      sortedArray = sortedArray.sort(firstBy("jobTitle"));
      if (sortOrder === "DESC") {
        sortedArray = sortedArray.reverse();
      }
    } else if (sortBy === "count") {
      // Sort by totalClicks
      sortedArray = sortedArray.sort(firstBy("totalClicks"));
      if (sortOrder === "DESC") {
        sortedArray = sortedArray.reverse();
        logger.debug("Dashboard:renderCampaignTable", "Reverse");
      }
    } else {
      // Sort by status
      sortedArray = sortedArray.sort(
        firstBy("statusSort").thenBy(function (el) {
          return -el.dateSorted;
        })
      );
      if (sortOrder === "ASC") {
        sortedArray = sortedArray.reverse();
      }
    }

    logger.debug("Dashboard:renderCampaignTable", "Sorting vars dashboard: ", {
      sortBy,
      sortOrder
    });

    // Is search active ?
    if (searchTerm !== "") {
      sortedArray = sortedArray.filter((el) => {
        const st = searchTerm.toLowerCase();

        if (
          el.companyName.toLowerCase().indexOf(st) > -1 ||
          el.jobTitle.toLowerCase().indexOf(st) > -1 ||
          (el.poNumber || "").toLowerCase().indexOf(st) > -1 ||
          (el.rechnungsNummer || "").toLowerCase().indexOf(st) > -1 ||
          (el.id || "").toLowerCase().indexOf(st) > -1
        ) {
          return true;
        }
        return false;
      });
    }

    let tr;

    const campaigns = filterCampaigns({
      campaigns: sortedArray,
      statuses: filters.statuses,
      companies: filters.companies,
      owners: filters.owners,
      dates: filters.dates
    });

    if (campaigns.length === 0 && searchTerm !== "") {
      tr = renderPlaceholder();
    } else {
      tr = renderRows(campaigns);
    }

    let sumClicks = 0;
    let sumImp = 0;
    let sumBudget = 0;
    let nrApplicants = 0;

    (campaigns || []).forEach((campaignData) => {
      const {
        applicantsCount,
        lastEditStage,
        lifeTimeBudget,
        locations,
        feedbackAnalysis,
        optimizeAnalysis,
        spotifyHumanVoice,
        channels,
        combinedBudget
      } = campaignData;

      sumClicks += getClicks(campaignData);

      const campaignBudget = getCostBreakdown({
        combinedBudget,
        locations,
        lifeTimeBudget,
        feedbackAnalysis,
        optimizeAnalysis,
        spotifyHumanVoice: channels?.includes("spotify") ? spotifyHumanVoice : 0,
        lastEditStage
      });

      sumBudget += campaignBudget;
      sumImp += getImpressions(campaignData);
      nrApplicants += applicantsCount || 0;
    });

    return (
      <div>
        <h2>
          {isAdminView ? t("Campaign overview") : t("Your campaigns")}{" "}
          <span>{campaigns.length}</span>
        </h2>

        <DashboardFilters
          userProfile={userProfile}
          isLoadingCampaigns={isLoadingCampaigns}
          campaignList={allCampaignList}
        />

        {isLoadingCampaigns && <LoadingView msg={`${t("Loading Campaign")}...`} showSpinner />}

        {allCampaignList && !isLoadingCampaigns && (
          <table className="dashboard__table">
            <thead>
              <tr className="table__top">
                <td
                  className={renderSortClass("name", false)}
                  onClick={() => handleSortBy("name", "ASC")}
                >
                  {t("Job Position")}
                </td>
                <td className="table__td-tiny" />
                <td className="table__td-small">{t("Budget")}</td>
                {organization && organization.atsAccess && (
                  <td className="right table__td-small">{t("Applicants")}</td>
                )}
                <td
                  className={renderSortClass("count", false)}
                  onClick={() => handleSortBy("count", "DESC")}
                >
                  {t("Clicks")}
                </td>
                <td className="table__td-small">{t("Impressions")}</td>
                <td className="table__td-small">{t("Likes")}</td>
                <td className="table__td-small">{t("CTR")}</td>
                <td className="table__td-small">{t("Status")}</td>
                <td
                  className={renderSortClass("createdDate", false)}
                  onClick={() => handleSortBy("createdDate", "DESC")}
                >
                  {t("Date created")}
                </td>
                <td className="table__td-small">{t("Created by")}</td>
                <td className="table__td-small">{t("Invoice No.")}</td>
                <td className="table__td-small">{t("Actions")}</td>
              </tr>
            </thead>
            <tbody>{tr}</tbody>
            <tfoot>
              <tr>
                <td className="table__td-clear" colSpan={2} />
                <td className="right">
                  <strong>
                    {t("SUM")}{" "}
                    {formatIntlNumber(sumBudget, {
                      language: userProfile?.language
                    })}{" "}
                    €
                  </strong>{" "}
                  <br />
                  <span className="dashboard__text-light">{t("Budget")}</span>
                </td>

                {organization && organization.atsAccess ? (
                  <>
                    <td className="right">
                      <strong>
                        {t("TOTAL")}{" "}
                        {formatIntlNumber(nrApplicants, {
                          language: userProfile?.language
                        })}
                      </strong>{" "}
                      <br />
                      <span className="dashboard__text-light">{t("Applicants")}</span>
                    </td>
                  </>
                ) : (
                  <td className="table__td-clear" />
                )}

                <td className="right">
                  <strong>
                    {t("SUM")}{" "}
                    {formatIntlNumber(sumClicks, {
                      language: userProfile?.language
                    })}
                  </strong>{" "}
                  <br />
                  <span className="dashboard__text-light">{t("Clicks")}</span>
                </td>

                <td className="right">
                  <strong>
                    {t("SUM")}{" "}
                    {formatIntlNumber(sumImp, {
                      language: userProfile?.language
                    })}
                  </strong>{" "}
                  <br />
                  <span className="dashboard__text-light">{t("Impressions")}</span>
                </td>
                <td className="table__td-clear" colSpan={7} />
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    );
  };

  const renderSortClass = (field, small) => {
    logger.debug("Dashboard:renderSortClass", "renderSortClass...", { field, sortOrder });
    return classNames({
      underline: true,
      active: sortBy === field,
      "table__td-clear": small,
      up: sortOrder === "ASC"
    });
  };

  const search = (e) => {
    // Add debounce
    CustomerSucessController.send(`Dashboard:search`, { searchTerm: e.target.value });
    setSearchTerm(e.target.value);
  };

  const renderSearchBox = () => {
    const classSearch = classNames({
      "dashboard__search-close": true,
      "dashboard__search-close--close": searchTerm === ""
    });

    return (
      <div className="dashboard__search">
        <input
          type="text"
          onChange={search}
          placeholder={t("Search")}
          value={searchTerm}
          className="dashboard__search-field"
        />
        <div
          className={classSearch}
          onClick={() => {
            setSearchTerm("");
          }}
        >
          x
        </div>
      </div>
    );
  };

  // Extract flags to avoid repeating the same logic in multiple places
  const getDashboardFlags = (userProfile) => {
    const { employerBranding } = flags;
    const employerBrandingFlag = employerBranding && userProfile.useEmployerBranding !== false;
    return { employerBrandingFlag };
  };

  const redirectBrandkit = () => {
    const { isAgency, hasCompletedBrandkitOnboarding } = organization;
    const { employerBrandingFlag } = getDashboardFlags(userProfile);

    // hasCompletedBrandkitOnboarding is present only on new organizations
    // we need to check false to show the brandkit popup
    const needBrandkit = hasCompletedBrandkitOnboarding === false;
    const requiredBKFF = !isAgency && needBrandkit;

    const showNewAssistantFlow = handleNewAssistantFlowFlag(organization);
    const newAssistantRoute = showNewAssistantFlow && !isAgency ? "/assistant" : "/new-campaign";

    const employerBrandingRoute = employerBrandingFlag ? "/choose-campaign" : newAssistantRoute;

    if (!requiredBKFF) {
      CustomerSucessController.send("Dashboard:CreateCampaign", {
        organizationId: organization.id,
        userId: userProfile.id
      });

      return history.push(employerBrandingRoute);
    }

    CustomerSucessController.send("Dashboard:BrandKitPopup", {
      organizationId: organization.id,
      userId: userProfile.id
    });

    const confirmAction = () => {
      CustomerSucessController.send("Dashboard:BrandKitRedirect", {
        organizationId: organization.id,
        userId: userProfile.id
      });

      Actions.toggleModal("brandKitRequired", false);
      history.push("/settings/brand-kit");
    };

    Actions.toggleModal("brandKitRequired", true, {
      headline: t("Strengthen your brand with a BrandKit"),
      widthVersion: "small",
      view: (
        <ModalConfirm
          showDescription={true}
          labelConfirm={t("Create BrandKit now")}
          description={
            <BrandKitMessageWrapper>
              {t(
                "A professional appearance is the key to the success of your campaigns. Your logo is more than just an image - it represents your company on every platform and attracts targeted attention."
              )}
              <BrandKitMessage>
                <strong>{t("Create your BrandKit now")}</strong>
                {", "}
                {t(
                  "to ensure your logo is displayed where it counts. This increases visibility and trust in your brand."
                )}
              </BrandKitMessage>
            </BrandKitMessageWrapper>
          }
          cancelledAction={() => {
            Actions.toggleModal("brandKitRequired", false);
          }}
          confirmedAction={() => confirmAction()}
        />
      )
    });
  };

  const redirectBudgetCheck = () => {
    const { isAgency } = organization;

    const showNewAssistantFlow = handleNewAssistantFlowFlag(organization);
    const newAssistantRoute =
      showNewAssistantFlow && !isAgency ? "/budget-check-assistant" : "/budget-check";

    return history.push(newAssistantRoute);
  };

  // Extract flags
  const { employerBrandingFlag } = getDashboardFlags(userProfile);

  // Extract user permissions
  const permissions = userProfile?.permissions || DEFAULT_USER_PERMISSIONS;

  logger.log("Dashboard:render", "dashboard flags and permissions", {
    employerBrandingFlag,
    permissions
  });

  logger.debug("Dashboard:render", "called", { organization, userProfile });

  if (!user) {
    logger.log(
      "warn:Dashboard:render",
      "Tried to access the dashboard, but not logged in, so redirect to Login"
    );
    return <Redirect to="/login" />;
  }

  const { hasPermissionsToRenderDashboard, hasPermissionsToRenderStatsHub } =
    getPermissions(userProfile);

  const handleEmptyStateClick = () => {
    window.open(window.config.baseUrlMessenger);
  };

  if (!hasPermissionsToRenderDashboard && hasPermissionsToRenderStatsHub) {
    return (
      <EmptyStateView
        title={t("Access denied - more Permissions needed")}
        description={t(
          "Your current user role does not have the necessary permissions to view the dashboard. You currently only have access to the applicant hub."
        )}
        imageSrc="/images/graphics/park-bench-doodling.svg"
        buttons={[
          {
            buttonLink: handleEmptyStateClick,
            buttonText: t("Switch to the applicant hub")
          }
        ]}
      />
    );
  }

  if (!campaignList) {
    return <LoadingView msg={`${t("Loading Campaign")}...`} showSpinner />;
  }
  if (!organization) {
    return <LoadingView msg={`${t("Loading Campaign")}...`} showSpinner />;
  }
  if (!userProfile) {
    return <LoadingView msg={`${t("Loading Campaign")}...`} showSpinner />;
  }

  const { disableCampaignBooking } = organization;
  const { hasPermissionsToViewBudget, hasPermissionsToCreateCampaigns } =
    getPermissions(userProfile);

  let christmasFever = false;
  posthogInstance?.onFeatureFlags(function () {
    if (posthogInstance.isFeatureEnabled("christmas-fever")) {
      christmasFever = true;
    }
  });

  const isGoodMills = organization?.id === "GvCen2rJ5wKq2uGZFviK";

  return (
    <div style={{ overflow: "hidden", position: "relative" }}>
      {christmasFever && <Snowfall snowflakeCount={250} radius={[0.5, 7.0]} />}
      <section className="dashboard fade-in">
        <div className="">
          {renderSearchBox()}
          {isAdminView ? null : (
            <>
              {hasPermissionsToCreateCampaigns && !disableCampaignBooking && !isGoodMills && (
                <BtnPrimary
                  mr={10}
                  type="primary"
                  size="medium"
                  onClick={() => redirectBrandkit()}
                  className="float-right"
                >
                  + {t("Create campaign")}
                </BtnPrimary>
              )}

              {hasPermissionsToViewBudget && !disableCampaignBooking && (
                <BtnPrimary
                  mr={10}
                  type="primary"
                  variant="outline"
                  size="medium"
                  onClick={() => redirectBudgetCheck()}
                  className="float-right"
                >
                  {t("+ Budget check")}
                </BtnPrimary>
              )}
            </>
          )}

          {renderCampaignTable()}

          <ReactTooltip
            place={"top"}
            effect={"solid"}
            type={"info"}
            multiline={true}
            className={"dashboard-tooltip"}
          />
        </div>
      </section>
    </div>
  );
};

export default withLDConsumer()(
  withRouter(
    withRefluxStore(
      AllCampaignsDashboard,
      [AdAssistantStore, StatusStore, DashboardStore, PosthogStore],
      [
        "organization",
        "userProfile",
        "user",
        "posthogInstance",
        "filters",
        "sortBy",
        "sortOrder",
        "campaignList",
        "errorLoadingCampaigns",
        "isAdminView",
        "orgaId",
        "flags",
        "history"
      ]
    )
  )
);
